import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getNotifications, getUserById, getFileUrl } from '../../services/apiService';

// Styles CSS pour les animations
const notificationStyles = `
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
  
  @keyframes bounce {
    0%, 100% {
      transform: translateY(0) scale(1);
    }
    50% {
      transform: translateY(-3px) scale(1.1);
    }
  }
`;

const AdminNavbar = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsRead, setNotificationsRead] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  useEffect(() => {
    const email = localStorage.getItem('userEmail');
    const name = localStorage.getItem('userName');
    const userId = localStorage.getItem('userId');
    
    if (email) {
      setUserEmail(email);
    }
    if (name) {
      setUserName(name);
    }

    // Charger les informations complètes de l'utilisateur (y compris l'avatar)
    const loadUserProfile = async () => {
      if (userId) {
        try {
          const userData = await getUserById(userId);
          if (userData) {
            if (userData.name) setUserName(userData.name);
            if (userData.avatar) {
              setUserAvatar(userData.avatar);
            } else {
            }
            // Mettre à jour l'ID dans localStorage si c'est un nouvel ObjectId
            if (userData.id && userData.id !== userId) {
              localStorage.setItem('userId', userData.id);
            }
          }
        } catch (error) {
        }
      }
    };

    loadUserProfile();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    // Fermer les dropdowns si on clique en dehors
    const handleClickOutside = (event) => {
      if (showProfileDropdown && !event.target.closest('.profile-dropdown-container')) {
        setShowProfileDropdown(false);
      }
      if (showNotifications && !event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };

    // Charger les notifications
    const loadNotifications = async () => {
      try {
        // Si les notifications ont été marquées comme lues (visite de la page commentaires), ne pas charger
        if (notificationsRead && location.pathname === '/admin/comments') {
          setUnreadCount(0);
          setPreviousUnreadCount(0);
          return;
        }
        
        const data = await getNotifications();
        if (data) {
          const newUnreadCount = data.unreadCount || 0;
          const newNotifications = data.notifications || [];
          
          // Si on est sur la page des commentaires, ne pas afficher les notifications
          if (location.pathname === '/admin/comments') {
            setUnreadCount(0);
            setPreviousUnreadCount(0);
            setNotificationsRead(true);
            return;
          }
          
          // Détecter les nouvelles notifications
          setUnreadCount(prevCount => {
            // Si de nouvelles notifications arrivent après avoir visité la page commentaires
            if (newUnreadCount > 0 && notificationsRead && newUnreadCount > prevCount) {
              // Réinitialiser l'état "lu" car il y a de nouvelles notifications
              setNotificationsRead(false);
              setPreviousUnreadCount(prevCount);
            }
            
            // Si les notifications ont été marquées comme lues et qu'il n'y a pas de nouvelles notifications
            if (notificationsRead && newUnreadCount <= prevCount) {
              return 0;
            }
            
            if (newUnreadCount > prevCount) {
              // Nouvelles notifications détectées
              setPreviousUnreadCount(prevCount);
              
              // Animation pour les nouvelles notifications
              if (newUnreadCount > prevCount && prevCount > 0) {
                // Il y a de nouvelles notifications
                const newNotificationsCount = newUnreadCount - prevCount;
              }
            } else if (newUnreadCount < prevCount) {
              // Le nombre a diminué, réinitialiser previousUnreadCount
              setPreviousUnreadCount(newUnreadCount);
            }
            
            // Si notificationsRead est true, retourner 0, sinon retourner le nouveau compteur
            return notificationsRead ? 0 : newUnreadCount;
          });
          
          setNotifications(newNotifications);
        }
      } catch (error) {
      }
    };

    // Charger les notifications au montage
    loadNotifications();
    
    // Actualiser les notifications toutes les 10 secondes pour plus de réactivité
    const interval = setInterval(loadNotifications, 10000);

    handleResize();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(interval);
    };
  }, [showProfileDropdown, showNotifications, notificationsRead, location.pathname]);

  // Écouter les changements de profil (quand l'utilisateur met à jour son profil)
  useEffect(() => {
    const handleStorageChange = () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        getUserById(userId).then(userData => {
          if (userData) {
            if (userData.name) setUserName(userData.name);
            if (userData.avatar) {
              setUserAvatar(userData.avatar);
            } else {
              setUserAvatar(null);
            }
            // Mettre à jour l'ID dans localStorage si c'est un nouvel ObjectId
            if (userData.id && userData.id !== userId) {
              localStorage.setItem('userId', userData.id);
            }
          }
        }).catch(error => {
        });
      }
    };

    // Écouter les événements de stockage personnalisés
    window.addEventListener('profileUpdated', handleStorageChange);
    
    // Vérifier périodiquement si le profil a été mis à jour (toutes les 3 secondes)
    const profileCheckInterval = setInterval(handleStorageChange, 3000);

    return () => {
      window.removeEventListener('profileUpdated', handleStorageChange);
      clearInterval(profileCheckInterval);
    };
  }, []);

  // Marquer les notifications comme lues quand l'admin visite la page des commentaires
  useEffect(() => {
    if (location.pathname === '/admin/comments') {
      setNotificationsRead(true);
      setUnreadCount(0);
      setPreviousUnreadCount(0);
    } else {
      // Si on quitte la page commentaires et qu'il y a de nouvelles notifications, réinitialiser
      // Cette logique est gérée dans loadNotifications
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  return (
    <>
      <style>{notificationStyles}</style>
      <nav style={{
      position: 'fixed',
      top: 0,
      left: isMobile ? '0' : (sidebarOpen ? '280px' : '70px'),
      right: 0,
      height: '64px',
      background: '#ffffff',
      borderBottom: '1px solid #e5e7eb',
      zIndex: 1000,
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '0 16px' : '0 24px',
      boxShadow: isScrolled 
        ? '0 1px 3px rgba(0, 0, 0, 0.1)' 
        : '0 1px 2px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Left side - Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Bouton toggle pour mobile */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={{
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              width: '40px',
              height: '40px',
              color: '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#96ca2d';
              e.target.style.color = '#96ca2d';
              e.target.style.boxShadow = '0 1px 3px rgba(150, 202, 45, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#d1d5db';
              e.target.style.color = '#6b7280';
              e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
            }}
          >
            <i className={`fas fa-${sidebarOpen ? 'times' : 'bars'}`} style={{ fontSize: '18px' }} />
          </button>
        )}
        
        {/* Breadcrumb */}
        <div style={{
          display: window.innerWidth > 768 ? 'flex' : 'none',
          alignItems: 'center',
          gap: '8px',
          color: '#6b7280',
          fontSize: '14px',
          fontWeight: '400'
        }}>
          <i className="fas fa-home" style={{ color: '#96ca2d', fontSize: '14px' }} />
          <span>/</span>
          <span style={{ color: '#96ca2d', fontWeight: '500' }}>Admin</span>
          <span>/</span>
          <span style={{ fontWeight: '500', color: '#374151' }}>
            {window.location.pathname.split('/').pop().charAt(0).toUpperCase() + 
             window.location.pathname.split('/').pop().slice(1)}
          </span>
        </div>
      </div>

      {/* Right side - User info and actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
      
        {/* Notifications */}
        <div className="notifications-container" style={{ position: 'relative' }}>
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              background: '#ffffff',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              width: '40px',
              height: '40px',
              color: '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              position: 'relative',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#96ca2d';
              e.currentTarget.style.color = '#96ca2d';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(150, 202, 45, 0.2)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              if (!showNotifications) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            <i 
              className="fas fa-bell" 
              style={{ 
                fontSize: '16px', 
                display: 'block', 
                zIndex: 1,
                animation: unreadCount > previousUnreadCount && !notificationsRead ? 'pulse 0.5s ease-in-out' : 'none'
              }} 
            />
            {unreadCount > 0 && !notificationsRead && (
              <div style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                minWidth: '14px',
                height: '14px',
                background: '#dc2626',
                borderRadius: '7px',
                border: '1.5px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: '600',
                color: 'white',
                padding: '0 3px',
                zIndex: 2,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                animation: unreadCount > previousUnreadCount ? 'bounce 0.5s ease-in-out' : 'none'
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </div>
            )}
          </button>

          {/* Dropdown Notifications */}
          {showNotifications && (
            <div style={{
              position: isMobile ? 'fixed' : 'absolute',
              top: isMobile ? '70px' : 'calc(100% + 8px)',
              right: isMobile ? '16px' : 0,
              left: isMobile ? '16px' : 'auto',
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              width: isMobile ? 'calc(100vw - 32px)' : '380px',
              maxWidth: isMobile ? 'calc(100vw - 32px)' : '380px',
              maxHeight: isMobile ? 'calc(100vh - 100px)' : '500px',
              zIndex: 1002,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Header */}
              <div style={{
                padding: isMobile ? '12px 16px' : '16px 20px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#f9fafb',
                gap: '8px',
                flexShrink: 0
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  minWidth: 0
                }}>
                  Notifications
                </h3>
                {unreadCount > 0 && (
                  <span style={{
                    background: '#dc2626',
                    color: 'white',
                    fontSize: isMobile ? '10px' : '12px',
                    fontWeight: '600',
                    padding: isMobile ? '2px 6px' : '2px 8px',
                    borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}>
                    {unreadCount} new{unreadCount > 1 ? '' : ''}
                  </span>
                )}
              </div>

              {/* Notifications List */}
              <div style={{
                overflowY: 'auto',
                maxHeight: isMobile ? 'calc(100vh - 180px)' : '400px',
                WebkitOverflowScrolling: 'touch'
              }}>
                {notifications.length === 0 ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <i className="fas fa-bell-slash" style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ margin: 0, fontSize: '14px' }}>No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification, index) => (
                    <div
                      key={notification.id}
                      onClick={() => {
                        if (notification.link) {
                          // Marquer les notifications comme lues quand on clique dessus
                          setNotificationsRead(true);
                          setUnreadCount(0);
                          navigate(notification.link);
                          setShowNotifications(false);
                        }
                      }}
                      style={{
                        padding: isMobile ? '12px 16px' : '16px 20px',
                        borderBottom: index < notifications.length - 1 ? '1px solid #f3f4f6' : 'none',
                        cursor: notification.link ? 'pointer' : 'default',
                        transition: 'all 0.2s ease',
                        background: 'white',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                      onMouseEnter={(e) => {
                        if (notification.link) {
                          e.target.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'white';
                      }}
                    >
                      <div style={{ display: 'flex', gap: isMobile ? '10px' : '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: isMobile ? '32px' : '40px',
                          height: isMobile ? '32px' : '40px',
                          borderRadius: '8px',
                          background: `${notification.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: notification.color,
                          fontSize: isMobile ? '14px' : '16px',
                          flexShrink: 0
                        }}>
                          <i className={notification.icon} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div style={{
                            fontSize: isMobile ? '13px' : '14px',
                            fontWeight: '600',
                            color: '#1f2937',
                            marginBottom: '4px',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {notification.title}
                          </div>
                          <div style={{
                            fontSize: isMobile ? '12px' : '13px',
                            color: '#6b7280',
                            marginBottom: '6px',
                            lineHeight: '1.5',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {notification.message}
                          </div>
                          <div style={{
                            fontSize: isMobile ? '10px' : '11px',
                            color: '#9ca3af',
                            marginTop: '4px'
                          }}>
                            {notification.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div style={{
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  borderTop: '1px solid #e5e7eb',
                  background: '#f9fafb',
                  textAlign: 'center',
                  flexShrink: 0
                }}>
                  <button
                    onClick={() => {
                      setNotificationsRead(true);
                      navigate('/admin/comments');
                      setShowNotifications(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#96ca2d',
                      fontSize: isMobile ? '12px' : '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    View All Notifications
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Profile */}
        <div 
          className="profile-dropdown-container"
          onClick={() => setShowProfileDropdown(!showProfileDropdown)}
          style={{
            display: window.innerWidth > 768 ? 'flex' : 'none',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 12px',
            background: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#96ca2d';
            e.target.style.boxShadow = '0 1px 3px rgba(150, 202, 45, 0.1)';
          }}
          onMouseLeave={(e) => {
            if (!showProfileDropdown) {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }
          }}
        >
          <div style={{
            width: '32px',
            height: '32px',
            background: userAvatar ? 'transparent' : '#96ca2d',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            overflow: 'hidden',
            border: '2px solid #ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative'
          }}>
            {userAvatar ? (
              <img 
                src={(() => {
                  const avatarUrl = getFileUrl(userAvatar);
                  return avatarUrl;
                })()}
                alt={userName || 'Admin'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  display: 'block'
                }}
                onError={(e) => {
                  // Si l'image ne charge pas, masquer l'image et afficher l'initiale
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.style.background = '#96ca2d';
                    // Créer un élément pour afficher l'initiale
                    if (!parent.querySelector('.avatar-fallback')) {
                      const fallback = document.createElement('span');
                      fallback.className = 'avatar-fallback';
                      fallback.textContent = userEmail ? userEmail.charAt(0).toUpperCase() : 'A';
                      fallback.style.cssText = 'color: white; font-size: 14px; font-weight: 600;';
                      parent.appendChild(fallback);
                    }
                  }
                }}
                onLoad={() => {
                }}
              />
            ) : (
              userEmail ? userEmail.charAt(0).toUpperCase() : 'A'
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div>   
              <div style={{
              
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0 0 1px 0'
              }}>
                {userName || 'Admin'}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                margin: 0
              }}>
                {userEmail || 'admin@medagricycle.com'}
              </div>
            </div>
            <i 
              className={`fas fa-chevron-down`} 
              style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                transition: 'transform 0.2s ease',
                transform: showProfileDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
              }} 
            />
          </div>

          {/* Dropdown */}
          {showProfileDropdown && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              minWidth: '180px',
              zIndex: 1002,
              overflow: 'hidden'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/');
                  setShowProfileDropdown(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#374151',
                  fontSize: '13px',
                  fontWeight: '400',
                  transition: 'all 0.2s ease',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.color = '#96ca2d';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#374151';
                }}
              >
                <i className="fas fa-home" style={{ fontSize: '12px', width: '14px' }} />
                <span>Home</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/admin/profile');
                  setShowProfileDropdown(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#374151',
                  fontSize: '13px',
                  fontWeight: '400',
                  transition: 'all 0.2s ease',
                  borderBottom: '1px solid #f3f4f6'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.color = '#96ca2d';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#374151';
                }}
              >
                <i className="fas fa-user-cog" style={{ fontSize: '12px', width: '14px' }} />
                <span>Manage Profile</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/admin/change-password');
                  setShowProfileDropdown(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#374151',
                  fontSize: '13px',
                  fontWeight: '400',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#f9fafb';
                  e.target.style.color = '#96ca2d';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#374151';
                }}
              >
                <i className="fas fa-key" style={{ fontSize: '12px', width: '14px' }} />
                <span>Change Password</span>
              </button>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{
            background: 'transparent',
            border: '1px solid #dc2626',
            borderRadius: '6px',
            padding: window.innerWidth > 768 ? '8px 12px' : '8px 10px',
            color: '#dc2626',
            cursor: 'pointer',
            fontSize: window.innerWidth > 768 ? '14px' : '12px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            textTransform: 'none'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#dc2626';
            e.target.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#dc2626';
          }}
        >
          <i className="fas fa-sign-out-alt" style={{ fontSize: '14px' }} />
          {window.innerWidth > 768 ? 'Logout' : ''}
        </button>
      </div>
    </nav>
    </>
  );
};

export default AdminNavbar;